// Report 화면들이 PivotChartComposed(공용 컴포넌트, `components/rechart/PivotChartComposed.js`,
// 수정 금지)를 그리드 클릭 드릴다운형 차트로 쓸 때 반복되는 로직 모음. FpGridSetting(grid.js)과
// 동일한 패턴 — 공용 파일은 안 건드리고 FP Report 전용 확장만 여기 둔다.
// fp-report-screen-guide.md §A-7 참고.

// PivotChartComposed는 x축 카테고리를 기본적으로 문자열 사전순 정렬한다
// (pivotXAxis.sort(), PivotChartComposed.js:88) — 주(W33..)/월(09, 10) 라벨이 섞이면
// "2026-09" < "2026-10" < "2026-W33"라 실제 시간 순서(주가 월보다 앞)와 어긋난다. 공용 파일은
// 고치지 않고, 이미 있는 확장 포인트 pivotConfig.xAxisSortMap(있으면 그 맵 순서로 재배열,
// PivotChartComposed.js의 sortXAxis 참고)을 chartHeader 원래 순서로 채워서 정렬을 덮어쓴다.
// 맵의 key 자체가 사전순으로 정렬되므로("0000" < "0001" < ...) zero-pad한 인덱스를 key로 쓴다
// (chartHeader 순서가 곧 원하는 x축 순서인 화면은 전부 이 함수를 pivotConfig.xAxisSortMap에 넣는다
// — 안 채우면 버킷 종류가 섞이는 순간(주+월, 월+일 등) 조용히 순서가 틀어진다).
export const buildXAxisSortMap = (labels) =>
  (labels ?? []).reduce((map, label, index) => {
    map[String(index).padStart(4, "0")] = label;
    return map;
  }, {});

// 그리드 클릭 드릴다운형 차트(그리드에서 클릭/필터링한 행 기준으로 차트를 다시 조회하는 공통
// 패턴)에서 반복되는 두 단계 중 ①단계 — groupKey 기준 합산. PivotChartComposed는 groupKey 값으로
// 범례를 자동 생성하고 그 목록을 data 행과 인덱스로 짝지어 읽으므로, groupKey가 중복되면(같은
// 값이 여러 행으로 나뉘어 옴) 범례와 값이 어긋난다 — 백엔드가 이미 집계해서 내려주더라도
// 방어적으로 한 번 더 합산한다. displayColorKey는 화면마다 필드명이 다르거나(예: `displayColor`
// vs `itemDisplayColor`) 없는 경우(예: ResrcWipProfile)도 있어 선택적으로 받는다.
export const mergeChartRowsByGroupKey = ({ rows, groupKey, chartHeader, displayColorKey }) => {
  const mergedByKey = new Map();
  for (const r of rows ?? []) {
    const keyValue = r[groupKey];
    const current = mergedByKey.get(keyValue);
    if (current) {
      r.pivotData?.forEach((value, index) => { current.pivotData[index] += value ?? 0; });
    } else {
      mergedByKey.set(keyValue, {
        [groupKey]: keyValue,
        ...(displayColorKey ? { [displayColorKey]: r[displayColorKey] } : {}),
        pivotData: (chartHeader ?? []).map((_, index) => r.pivotData?.[index] ?? 0)
      });
    }
  }
  return [...mergedByKey.values()];
};

// ②단계 — ①의 합산 결과를 PivotChartComposed가 요구하는 형태로 펼친다: chartData(point 배열,
// chartHeader 각 항목이 컬럼이 되는 wide 포맷)와 chartSeries(범례, groupKey 값과 1:1). 화면마다
// 다른 부분(type/groupName/stackId)만 seriesOptions로 받는다.
export const buildChartDataAndSeries = ({ merged, groupKey, chartHeader, displayColorKey, seriesOptions }) => {
  const chartData = merged.map(r => {
    const point = { [groupKey]: r[groupKey] };
    (chartHeader ?? []).forEach((header, index) => { point[header] = r.pivotData[index]; });
    return point;
  });

  const { type = "bar", groupName, stackId } = seriesOptions ?? {};
  const chartSeries = merged.map(r => ({
    field: r[groupKey],
    text: r[groupKey],
    type,
    groupName,
    typeOptions: displayColorKey ? { stackId, fill: r[displayColorKey] } : { stackId }
  }));

  return { chartData, chartSeries };
};

// 클릭/드릴다운형 차트 타이틀(<Typography>) 공통 스타일 — fp-report-screen-guide.md §A-7 확정값.
// old Chart.js title 플러그인 기본값(font.size:17/weight:'bold')을 참고해 이식했으나, splitter
// 바로 아래라는 레이아웃 차이 때문에 padding은 old 그대로 옮기지 않고 조정했다(pt:10px/pb:2px),
// 굵기도 실사용해보니 bold(700)가 과해서 600으로 낮췄다.
export const CHART_TITLE_SX = { textAlign: "center", fontSize: "17px", fontWeight: 600, pt: "10px", pb: "2px" };
